import {Component, DestroyRef, Inject, NgZone, OnDestroy, OnInit, Renderer2, ViewEncapsulation, DOCUMENT} from '@angular/core';
import {RibbonComponent} from './ribbon/ribbon.component';
import {AppConfigService} from '../common/service/app-config.service';
import {SpatialUtilService} from './services/spatial-util.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {MapToolsService} from './services/map-tools.service';
import {MapService} from './services/map.service';
import {LineLayerService} from './layers/line-layer.service';
import {PolylineLayerService} from './layers/polyline-layer.service';
import {PointLayerService} from './layers/point-layer.service';
import {ControlPointLayerService} from './layers/control-point-layer.service';
import {AngleLayerService} from './layers/angle-layer.service';
import {ConstructPointLayerService} from './layers/construct-point-layer.service';
import {ClippingLayerService} from './layers/clipping-layer.service';
import {AnnotationLayerService} from './layers/annotation-layer.service';
import {EnlargementLayerService} from './layers/enlargement-layer.service';
import {ConstructLineLayerService} from './layers/construct-line-layer.service';
import {PolygonLayerService} from './layers/polygon-layer.service';
import {PrintAreaLayerService} from './layers/print-area-layer.service';
import {LotLayerService} from './layers/lot-layer.service';
import {RoadLayerService} from './layers/road-layer.service';
import {InterestLayerService} from './layers/interest-layer.service';
import {RibbonService} from './services/ribbon.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {PDF_CREATION_METHOD, PLAN_VALIDATION_STAGE, RULE_OUTCOME} from '../common/constants';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {AlertModalService} from '../common/component/modal';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {NgEventBus} from 'ng-event-bus';
import {NLR_TABS as LeftComponentTabs} from '../common/constants';
import {catchError, delay, distinctUntilChanged, first, fromEvent, map, merge, Observable, of, repeat, Subscription, tap} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';

import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SpatialSequenceService} from '../common/service/spatial-sequence.service';
import {isDefined, isUndefined} from '../common/service/data-util';
import {WidgetService} from './services/widget.service';
import {GraphicSelectionService} from './services/graphic-selection.service';
import {InterestReconciliationService} from '../common/service/interest-reconciliation.service';
import {HamburgerService} from '../templates/hamburger/hamburger.service';
import {DisplayPreferencesService} from '../common/service/display-preferences.service';
import {PrintAreaService} from '../common/service/print-area.service';
import {SpatialSnapshotService} from './snapshot/spatial-snapshot.service';
import {AffectedLotReconciliationService} from '../common/service/affected-lot-reconciliation.service';
import {MapModeService} from './services/map-mode.service';
import {EsriModuleService} from './esri-nlr/services/esri-module.service';
import {FeaturesRepositoryService} from '../common/service/features-repository.service';
import {AttributeViewerService} from './widgets/attribute-viewer/attribute-viewer.service';
import {AffectedLotService} from './services/features/affected-lot.service';
import {EnlargementEventsService} from './widgets/enlargement/services/enlargement-events.service';
import {WindowWidgetsService} from './window-widgets/window-widgets.service';
import {ConstructionToolModelService} from './widgets/construction-tools/construction-tool/services/construction-tool-model.service';
import {SelectionUtilityService} from './services/selection-utility.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Component({
    selector: 'nlr-design',
    imports: [RibbonComponent],
    templateUrl: './design.component.html',
    styleUrl: './design.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DesignComponent implements OnDestroy, OnInit {
    private spatialSnapshotRestoredDeregister: Subscription;

    initialized: boolean = false;
    keyMouseSub: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private appConfigService: AppConfigService,
        private attributeViewerService: AttributeViewerService,
        private spatialUtilService: SpatialUtilService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private mapService: MapService,
        private ribbonService: RibbonService,
        private lineLayerService: LineLayerService,
        private polylineLayerService: PolylineLayerService,
        private pointLayerService: PointLayerService,
        private controlPointLayerService: ControlPointLayerService,
        private constructPointLayerService: ConstructPointLayerService,
        private constructionToolModelService: ConstructionToolModelService,
        private clippingLayerService: ClippingLayerService,
        private angleLayerService: AngleLayerService,
        private annotationLayerService: AnnotationLayerService,
        private constructLineLayerService: ConstructLineLayerService,
        private polygonLayerService: PolygonLayerService,
        private enlargementLayerService: EnlargementLayerService,
        private printAreaLayerService: PrintAreaLayerService,
        private lotLayerService: LotLayerService,
        private roadLayerService: RoadLayerService,
        private interestLayerService: InterestLayerService,
        private planLodgementService: PlanLodgementService,
        private planDetailsService: NlrPlanDetailService,
        private alertModalService: AlertModalService,
        private notificationAlertsService: NotificationAlertsService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef,
        private spatialSequenceService: SpatialSequenceService,
        private widgetService: WidgetService,
        private graphicSelectionService: GraphicSelectionService,
        private interestReconciliationService: InterestReconciliationService,
        private hamburgerService: HamburgerService,
        private displayPreferencesService: DisplayPreferencesService,
        private printAreaService: PrintAreaService,
        private spatialSnapshotService: SpatialSnapshotService,
        private affectedLotReconciliationService: AffectedLotReconciliationService,
        private mapModeService: MapModeService,
        private esriModuleService: EsriModuleService,
        private featuresRepositoryService: FeaturesRepositoryService,
        private ngZone: NgZone,
        private selectionUtilityService: SelectionUtilityService,
        // Note! Do not remove AffectedLotService and EnlargementEventsService from the constructor.
        // It is not used directly but called through event bus in several places.
        // Removing it will break multiple components.
        // The service must be injected somewhere to ensure it gets initialized and its constructor is called.
        // Within the constructor, several event bus subscriptions are set up. If the service isn't initialized, these subscriptions won't be created
        // and, as a result, won't be triggered.
        private affectedLotService: AffectedLotService,
        private enlargementEventsService: EnlargementEventsService,
        private windowWidgetsService: WindowWidgetsService
    ) {}

    ngOnInit() {
        this.init();
        this.selectionUtilityService.init(this.document, this.ngZone);
        this.ngZone.runOutsideAngular(() => {
            const keydownObservable = fromEvent<KeyboardEvent>(this.document, 'keydown').pipe(
                filter(event => !event.repeat),
                map(event => ({event, type: 'keydown'}))
            );

            console.debug('%s - initSpatialSnapshots()', this.title);
            this.spatialSnapshotRestoredDeregister = this.eventBus
                .on('spatialSnapshotRestored')
                .pipe(
                    tap(() => this.clearSelectedFeatures()),
                    switchMap(() => this.reconcilePlanInformation()),
                    tap(() => this.eventBus.cast('addEnlargementOnMapModeList')),
                    catchError(err => {
                        console.error('[spatialSnapshotRestored] refresh failed', err);
                        return of(null);
                    }),
                    repeat(),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();

            const keyupObservable = fromEvent<KeyboardEvent>(this.document, 'keyup').pipe(map(event => ({event, type: 'keyup'})));

            const mouseDownObservable = fromEvent<MouseEvent>(this.document, 'mousedown').pipe(map(event => ({event, type: 'mousedown'})));

            const mouseUpObservable = fromEvent<MouseEvent>(this.document, 'mouseup').pipe(map(event => ({event, type: 'mouseup'})));

            this.keyMouseSub = merge(keydownObservable, keyupObservable, mouseDownObservable, mouseUpObservable)
                .pipe(
                    distinctUntilChanged(
                        (a, b) =>
                            a.type === b.type &&
                            (a.event instanceof KeyboardEvent && b.event instanceof KeyboardEvent
                                ? a.event.key === b.event.key
                                : a.event instanceof MouseEvent && b.event instanceof MouseEvent
                                  ? a.event.button === b.event.button
                                  : false)
                    )
                )
                .subscribe(payload => {
                    this.ngZone.runOutsideAngular(() => {
                        switch (payload.type) {
                            case 'keydown':
                                this.keydownHandler(payload.event as KeyboardEvent);
                                break;
                            case 'keyup':
                                this.keyupHandler(payload.event as KeyboardEvent);
                                break;
                            case 'mousedown':
                                this.mousedownHandler(payload.event as MouseEvent);
                                break;
                            case 'mouseup':
                                this.mouseupHandler(payload.event as MouseEvent);
                                break;
                        }
                    });
                });
            this.initializeCapturePhaseEventListeners();
        });
    }

    ngOnDestroy(): void {
        this.keyMouseSub?.unsubscribe();
        Object.keys(this.capturePhaseEventListeners).forEach(type => {
            this.document.removeEventListener(type, this.capturePhaseEventListeners[type]);
        });
        this.selectionUtilityService.destroy();
        this.windowWidgetsService.closeAll();
    }

    title = 'DesignController';
    getDesignProperties = () => this.appConfigService.getDesignProperties();
    private capturePhaseEventListeners: {[type: string]: (event: Event) => void} = {};
    setup() {
        console.debug(`${this.title} - setup`);
        const subs = of(null)
            .pipe(
                tap(() => {
                    this.setupDesignFramework(true);
                    this.setMapHeight();
                }),
                tap(() => this.spatialSequenceService.sync()),
                switchMap(() => this.reconcilePlanInformation()),
                switchMap(() => this.mapService.moveToExtent(this.planModelService.getModel().formerTenures)),
                tap(() => {
                    console.debug(`${this.title} - setup - complete`);
                    this.eventBus.cast('switchCadastre', this.mapToolsService.isActive('SHOW_HIDE_CADASTRE') && !this.mapService.isPrintPreview());

                    // delay setting initial map mode otherwise labels are not displayed
                    setTimeout(() => {
                        this.mapService.setMapMode(false, this.displayPreferencesService.displayPreferences);
                        this.eventBus.cast('addEnlargementOnMapModeList');
                    }, 500);
                })
            )
            .subscribe();
        this.eventBus.cast('nlr:busy', subs);
        this.graphicSelectionService.featureClassSelectionChanged();
    }

    init() {
        this.mapModeService.init();
        this.constructionToolModelService.clearAllToolsAttrs();
        this.mapToolsService.init(
            'PAN',
            () => {
                if (!this.spatialUtilService.map?.isPan) {
                    this.spatialUtilService.map.enablePan();
                }
            },
            () => {
                if (this.spatialUtilService.map?.isPan) {
                    this.spatialUtilService.map.disablePan();
                }
            }
        );
        this.eventBus
            .on('validate')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.validate();
            });
        this.eventBus
            .on('setupDesignFramework')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} : on setupDesignFramework`);
                this.spatialSequenceService.init();
                this.spatialSequenceService.sync();
                this.setupDesignFramework(false);
            });
        this.eventBus
            .on('initiateDesignContent')
            .pipe(
                tap((meta: any) => {
                    console.debug(`${this.title} : initiateDesignContent by watch`);
                }),
                switchMap((meta: any) => {
                    this.mapService.init();
                    const {reload, splitter, initProjection} = meta.data as any;
                    const errorMessages = [];

                    if (isUndefined(this.planModelService.getModel().spatialSettings.projectedCoordSystem)) {
                        errorMessages.push('The Design user interface cannot be displayed until the projected coordinate system has been specified.');
                    }
                    if (isUndefined(this.planModelService.getModel().planNumber)) {
                        errorMessages.push('The Design user interface cannot be displayed until the plan number has been entered.');
                    }
                    if (isUndefined(this.planModelService.getModel().surveyMethod)) {
                        errorMessages.push('The Design user interface cannot be displayed until the survey method has been selected.');
                    }

                    if (errorMessages.length > 0) {
                        this.eventBus.cast('resetTabs');
                        this.alertModalService.createAlertMessage(errorMessages, 'Ok');
                        return of(null); // Stop further execution
                    }

                    const isHamburgerOpen = this.hamburgerService.isHamburgerVisible();
                    if (isHamburgerOpen) {
                        this.eventBus.cast('showHamburger');
                    }
                    if (splitter) {
                        this.eventBus.cast('nlr:collapsePanel', 'left');
                    }

                    if (!this.initialized || isUndefined(this.mapService.map) || reload || !this.mapService.onDesignMode) {
                        this.initialized = true;
                        this.widgetService.init();
                        return of(null).pipe(
                            delay(500),
                            tap(() => this.setMapHeight()),
                            switchMap(() => (initProjection ? this.spatialUtilService.init() : of(null))),
                            tap(() => this.setup())
                        );
                    }

                    if (this.widgetService.isWidgetActive('IdentifyFeatures')) {
                        this.eventBus.cast('closeIdentifiedFeatures', true);
                    }

                    return this.mapService
                        .moveToExtent(this.planModelService.getModel().formerTenures)
                        .pipe(switchMap(() => this.reconcilePlanInformation()));
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    crtlKeyDown = false;
    altKeyDown = false;
    shiftKeyDown = false;
    mouseDown = false;
    toggleZoom = false;

    keyNavigationDown = false;

    getLineSvgDefs() {
        return this.lineLayerService.getSvgDefs();
    }

    getLineSvgStyle() {
        return this.lineLayerService.getSvgStyle();
    }

    getStaticPointSvgStyle() {
        return this.pointLayerService.getStaticSvgStyle();
    }

    getPointSvgStyle() {
        return this.pointLayerService.getSvgStyle();
    }

    getControlPointSvgStyle() {
        return this.controlPointLayerService.getSvgStyle();
    }

    getConstructPointSvgStyle() {
        return this.constructPointLayerService.getSvgStyle();
    }

    getAnnotationSvgStyle() {
        return this.annotationLayerService.getSvgStyle();
    }

    getAngleSvgStyle() {
        return this.angleLayerService.getSvgStyle();
    }

    getConstructLineSvgStyle() {
        return this.constructLineLayerService.getSvgStyle();
    }

    getPolylineSvgStyle() {
        return this.polylineLayerService.getSvgStyle();
    }

    getPolygonSvgStyle() {
        return this.polygonLayerService.getSvgStyle();
    }

    getEnlargementSvgStyle() {
        return this.enlargementLayerService.getSvgStyle();
    }

    getClippingSvgStyle() {
        return this.clippingLayerService.getSvgStyle();
    }

    getPrintAreaSvgStyle() {
        return this.printAreaLayerService.getSvgStyle();
    }

    getLotSvgStyle() {
        return this.lotLayerService.getSvgStyle();
    }

    getRoadSvgStyle() {
        return this.roadLayerService.getSvgStyle();
    }

    getInterestSvgStyle() {
        return this.interestLayerService.getSvgStyle();
    }
    initializeCapturePhaseEventListeners(): void {
        this.capturePhaseEventListeners['keydown'] = (event: KeyboardEvent) => {
            this.ngZone.runOutsideAngular(() => {
                if (!this.planDetailsService.isDesignTabSelected()) {
                    return;
                }

                const element = this.document.querySelector('#mainMap');
                if (!event.repeat && this.isVisible(element)) {
                    // "+", "-" or up/down/left/right arrow key - enable keyboard navigation
                    if (['+', '-'].includes(event.key) || [37, 38, 39, 40].includes(event.keyCode)) {
                        this.mapService.map.enableKeyboardNavigation();
                    } else {
                        this.mapService.map.disableKeyboardNavigation();
                    }
                }
            });
        };

        this.document.addEventListener('keydown', this.capturePhaseEventListeners['keydown'], true); // Use capture phase
        this.ngZone.runOutsideAngular(() => {
            ['pointerup', 'pointerdown', 'pointermove', 'pointerover', 'pointerout'].forEach(type => {
                this.capturePhaseEventListeners[type] = () => {
                    this.document.body.style.pointerEvents = 'auto';
                };
                this.document.addEventListener(type, this.capturePhaseEventListeners[type], true); // Use capture phase
            });
        });
    }

    removeEventListeners(): void {
        // Remove all event listeners
        Object.keys(this.capturePhaseEventListeners).forEach(type => {
            this.document.removeEventListener(type, this.capturePhaseEventListeners[type], true);
        });
        this.capturePhaseEventListeners = {};
    }
    panVertical(val) {
        const mapCenter = this.mapService.map.extent.getCenter();
        const planCenter = this.mapService.map.toScreen(new (window as any).esri.geometry.Point(mapCenter.x, mapCenter.y));

        this.mapService.map.__panStart(planCenter.x, planCenter.y);
        this.mapService.map.__pan(0, val * planCenter.y);
        this.mapService.map.__panEnd(0, val * planCenter.y);
    }

    deactivatePan() {
        if (this.mapToolsService.isActive('PAN')) {
            this.mapToolsService.deactivate('PAN');
            this.spatialUtilService.map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
        }
    }

    private keydownHandler(event: KeyboardEvent): void {
        if (event.repeat) {
            return;
        }

        if (!this.planDetailsService.isDesignTabSelected()) {
            return;
        }

        const element = this.document.querySelector('#mainMap');
        if (this.isVisible(element)) {
            this.altKeyDown = event.altKey;
            this.crtlKeyDown = this.attributeViewerService.ctrlKeyDown = event.ctrlKey;
            this.shiftKeyDown = this.attributeViewerService.shiftKeyDown = event.shiftKey;

            if (this.altKeyDown) {
                event.stopPropagation();
                event.preventDefault();

                if (this.mapModeService.viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE) {
                    this.mapService.map.setMapCursor(this.mapService.MOVE_BLACK_CURSOR);
                } else {
                    this.mapService.map.setMapCursor(this.mapService.MOVE_WHITE_CURSOR);
                }
            }

            // Page down key - pan down
            if (event.keyCode === 33) {
                this.panVertical(1);
            }

            // Page up key - pan up
            if (event.keyCode === 34) {
                this.panVertical(-1);
            }
        }
        this.mapToolsService.keydownHandler(event);
    }
    private keyupHandler(event: KeyboardEvent): void {
        if (!this.planDetailsService.isDesignTabSelected()) {
            return;
        }

        const element = this.document.getElementById(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID);
        if (this.isVisible(element)) {
            this.altKeyDown = event.altKey;
            this.crtlKeyDown = this.attributeViewerService.ctrlKeyDown = event.ctrlKey;
            this.shiftKeyDown = this.attributeViewerService.shiftKeyDown = event.shiftKey;

            if (!this.mouseDown) {
                this.deactivatePan();
            }
        }
        this.mapToolsService.keyupHandler(event);
    }

    private isVisible(element: Element) {
        if (!isDefined(element)) {
            return false;
        }
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.clientHeight > 0 && element.clientWidth > 0;
    }

    private mousedownHandler(event: MouseEvent): void {
        const element = this.document.getElementById(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID);
        if (this.isVisible(element)) {
            this.mouseDown = true;
        }
    }

    private mouseupHandler(event: MouseEvent): void {
        const element = this.document.getElementById(this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID);
        if (this.isVisible(element)) {
            this.mouseDown = false;
            if (!this.altKeyDown && !event.altKey) {
                this.deactivatePan();
            }
        }
    }

    validate() {
        console.debug(this.title + ' : validate');

        let stage = '';

        if (this.planLodgementService.hasMinimumPlanDetails() && this.planLodgementService.designApplicable()) {
            if (this.planModelService.readonly) {
                stage = PLAN_VALIDATION_STAGE.LODGEMENT_DESIGN;
                const subs = this.planDetailsService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_DESIGN).subscribe(res => {
                    this.eventBus.cast('nlr:selectTab', LeftComponentTabs.DESIGN);
                    this.eventBus.cast('nlr:collapsePanel', 'right');
                });
                this.eventBus.cast('nlr:busy', subs);
            } else {
                this.planLodgementService.increaseValidationAttempt();
                stage = 'LODGEMENT_DESIGN';
                const subs = this.planModelService
                    .save()
                    .pipe(
                        switchMap(() => this.planDetailsService.runValidationsForStage('LODGEMENT_DESIGN')),
                        catchError(reason => {
                            console.error(`${this.title} - :`, reason);
                            return this.alertModalService.createAlertMessage(reason);
                        }),
                        tap(() => {
                            if (
                                this.planDetailsService
                                    .getValidationResults()
                                    .getValue()
                                    .filter(
                                        result =>
                                            (result.type === RULE_OUTCOME.CRITICAL || result.type === RULE_OUTCOME.ERROR) && result.stage === stage
                                    ).length
                            ) {
                                if (!this.notificationAlertsService.messages.filter(message => message.subType == 'DesignRules').length)
                                    this.notificationAlertsService.addMessage({
                                        text: 'The Design page has business rule errors!',
                                        subType: 'DesignRules'
                                    });
                            }
                        })
                    )
                    .pipe(first())
                    .subscribe(() => {
                        this.eventBus.cast('nlr:selectTab', LeftComponentTabs.DESIGN);
                        this.eventBus.cast('nlr:collapsePanel', 'show');
                    });
                this.eventBus.cast('nlr:busy', subs);
            }
            //?? this.notificationAlertsService.validateDesign = true;
        }
    }

    clearSelectedFeatures() {
        console.debug(this.title + ' : clearSelectedFeatures');
        this.ribbonService.clearSelectedFeatures();
    }

    setMapHeight() {
        console.debug(this.title + ' : setMapHeight');
        const mapElement = this.document.querySelector('#' + this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.ID);
        const splitAreaElement = this.document.getElementById('right-split-area');
        const height = (splitAreaElement as HTMLElement).offsetHeight;
        (mapElement as HTMLElement).style.height = height - 45 + 'px';
    }

    setupDesignFramework(designMode) {
        console.debug(`${this.title} : setupDesignFramework`);
        this.ngZone.runOutsideAngular(args => {
            this.mapService.initializeMap(
                this.planModelService.getModel().formerTenures,
                this.printAreaService.getReferenceScaleDivisor(),
                designMode,
                () => {}
            );
        });
    }

    reconcilePlanInformation() {
        console.debug(`${this.title} - reconcilePlanInformation`);
        if (this.planModelService.readonly) {
            return of(null);
        }
        return this.interestReconciliationService
            .reconcileInterests(this.planModelService.getModel().formerTenures, this.planModelService.getModel().interests)
            .pipe(
                switchMap(() => this.affectedLotReconciliationService.process()),
                tap(() => {
                    console.debug(`${this.title} - reconcilePlanInformation - complete`);
                    const {exemptedRoads, lgaApprovedUnnamedRoad, pdfCreationMethod, roads, roadWidening} = this.planModelService.getModel();
                    const isRoadWidening = pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL && roadWidening;
                    const isRoadLGA = pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL && lgaApprovedUnnamedRoad;
                })
            );
    }
}
